import { useCallback, useEffect, useRef, useState } from 'react';

import { searchCollection } from '../services/collectionSearch';

/**
 * Merge a later page into the cards already held (SCHEMA.md: "a client
 * appending page 2 merges by `id`"). A card keeps its place; its items are
 * appended, and its count and name come from the newest answer.
 */
export function mergeCards(held, incoming) {
	const next = held.map(card => ({ ...card }));
	(incoming || []).forEach((card) => {
		const at = next.findIndex(candidate => candidate.id === card.id);
		if (at === -1) next.push({ ...card, items: [...(card.items || [])] });
		else {
			const seen = new Set(next[at].items.map(item => item.id));
			next[at] = {
				...next[at],
				...card,
				items: [...next[at].items, ...(card.items || []).filter(item => !seen.has(item.id))],
			};
		}
	});
	return next;
}

/** What went wrong, in the server's own words when it said any. */
export function searchFailure(error) {
	const body = error?.response?.data;
	const status = error?.response?.status || null;
	const message = body && typeof body.message === 'string' && body.message.trim() ? body.message.trim() : '';
	return {
		status,
		code: body?.code || null,
		// A refusal of the term (400) is the person's to edit; the rest is retried.
		refusal: status === 400,
		message: message || (status === 404 ? 'This collection could not be searched.' : "The search couldn't be run. Trying again may help."),
	};
}

const EMPTY = Object.freeze({
	cards: [], total: null, counted: true, skippedProtected: false, cardsCapped: false, nextCursor: null,
});

/**
 * The grouped search of one collection for one term (KPR-355). `term` is the
 * trimmed term or '' (nothing is asked for ''). The newest question wins: an
 * answer for an older term, or an older page, is dropped.
 *
 *   { answer, loading, loadingMore, error, loadMore, reload }
 *
 * `answer` is null until the first page of this term has arrived; while a new
 * term loads, the previous answer stays (`loading` true) so the page never
 * blanks between keystrokes.
 */
export default function useCollectionSearch(collectionId, term, { enabled = true } = {}) {
	const [answer, setAnswer] = useState(null);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState(null);
	const ticket = useRef(0);
	const answerRef = useRef(null);
	answerRef.current = answer;

	const run = useCallback((cursor) => {
		const mine = ticket.current + 1;
		ticket.current = mine;
		if (cursor) setLoadingMore(true);
		else setLoading(true);
		setError(null);
		searchCollection(collectionId, { q: term, cursor })
			.then((data) => {
				if (ticket.current !== mine) return;
				const page = { ...EMPTY, ...(data || {}), cards: Array.isArray(data?.cards) ? data.cards : [] };
				setAnswer(cursor && answerRef.current
					? { ...page, cards: mergeCards(answerRef.current.cards, page.cards), term }
					: { ...page, term });
			})
			.catch((failure) => {
				if (ticket.current !== mine) return;
				setError(searchFailure(failure));
			})
			.finally(() => {
				if (ticket.current !== mine) return;
				setLoading(false);
				setLoadingMore(false);
			});
	}, [collectionId, term]);

	useEffect(() => {
		if (!enabled || !collectionId || !term) {
			ticket.current += 1;
			setAnswer(null);
			setError(null);
			setLoading(false);
			setLoadingMore(false);
			return undefined;
		}
		run(null);
		return () => { ticket.current += 1; };
	}, [enabled, collectionId, term, run]);

	const loadMore = useCallback(() => {
		const cursor = answerRef.current?.nextCursor;
		if (cursor) run(cursor);
	}, [run]);

	const reload = useCallback(() => run(null), [run]);

	return { answer, loading, loadingMore, error, loadMore, reload };
}
